// src/components/students/StudentForm.tsx

import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {
    Alert,
    Box,
    Chip,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import {
    CurrencyRuble as MoneyIcon,
    Email as EmailIcon,
    Group as GroupIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Telegram as TelegramIcon,
    VpnKey as VkIcon,
} from '@mui/icons-material';
import {CreateStudentDto, Group, Student} from '../../types';

export interface StudentFormRef {
    submit: () => void;
}

interface StudentFormProps {
    initialData?: Student | null;
    groups: Group[];
    onSubmit: (data: CreateStudentDto) => void;
    loading?: boolean;
}

const StudentForm = forwardRef<StudentFormRef, StudentFormProps>(({
                                                                      initialData,
                                                                      groups,
                                                                      onSubmit,
                                                                      loading = false,
                                                                  }, ref) => {
    const [formData, setFormData] = useState<CreateStudentDto>({
        fullName: '',
        phone: '',
        vk: '',
        telegram: '',
        email: '',
        pricePerLesson: 0,
        groupId: null,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Признак того, что студент уже зарегистрирован
    const isRegistered = !!(initialData && initialData.hasAccount);

    // При создании — только базовые поля
    // При редактировании — базовые + (если зарегистрирован) контакты
    const showContacts = isRegistered;

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    useEffect(() => {
        if (initialData) {
            setFormData({
                fullName: initialData.fullName || '',
                phone: initialData.phone || '',
                vk: initialData.vk || '',
                telegram: initialData.telegram || '',
                email: initialData.email || '',
                pricePerLesson: initialData.pricePerLesson || 0,
                groupId: initialData.groupId || null,
            });
        } else {
            setFormData({
                fullName: '',
                phone: '',
                vk: '',
                telegram: '',
                email: '',
                pricePerLesson: 0,
                groupId: null,
            });
            setErrors({});
        }
    }, [initialData]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const numValue = value === '' ? 0 : Number(value);
        setFormData(prev => ({ ...prev, [name]: numValue }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent<number | null>) => {
        const value = e.target.value as number | null;
        setFormData(prev => ({ ...prev, groupId: value }));
        if (errors.groupId) {
            setErrors(prev => ({ ...prev, groupId: '' }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.fullName || formData.fullName.trim().length < 2) {
            newErrors.fullName = 'ФИО должно содержать минимум 2 символа';
        }

        if (formData.pricePerLesson < 0) {
            newErrors.pricePerLesson = 'Цена не может быть отрицательной';
        }

        // Валидация контактов — только если они показаны
        if (showContacts) {
            if (formData.phone && formData.phone.trim().length > 0 && formData.phone.trim().length < 10) {
                newErrors.phone = 'Введите корректный номер телефона';
            }

            if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = 'Введите корректный email';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            // Формируем DTO
            const cleanData: CreateStudentDto = {
                fullName: formData.fullName.trim(),
                pricePerLesson: formData.pricePerLesson,
                groupId: formData.groupId,
            };

            // Контакты отправляем только если студент зарегистрирован
            if (showContacts) {
                cleanData.phone = formData.phone?.trim() || null;
                cleanData.vk = formData.vk?.trim() || null;
                cleanData.telegram = formData.telegram?.trim() || null;
                cleanData.email = formData.email?.trim() || null;
            }

            onSubmit(cleanData);
        }
    };

    const getGroupName = (groupId: number | null) => {
        if (!groupId) return 'Без группы';
        const group = groups.find(g => g.id === groupId);
        return group ? group.name : 'Группа не найдена';
    };

    return (
        <Box sx={{ mt: 1 }}>
            {/* Информация при редактировании */}
            {initialData && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" component="span">
                            Редактирование: <strong>{initialData.fullName}</strong>
                        </Typography>
                        {!initialData.isActive && (
                            <Chip label="Неактивен" color="error" size="small" />
                        )}
                        {initialData.hasAccount ? (
                            <Chip label="Зарегистрирован" color="success" size="small" />
                        ) : (
                            <Chip label="Не зарегистрирован" color="warning" size="small" />
                        )}
                    </Box>
                </Alert>
            )}

            {/* Предупреждение при создании */}
            {!initialData && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Ученик будет создан без учётной записи
                    </Typography>
                    <Typography variant="caption">
                        После создания вы получите ссылку-приглашение. Ученик сам зарегистрируется
                        и заполнит свои контактные данные.
                    </Typography>
                </Alert>
            )}

            {/* Предупреждение, если ещё не зарегистрирован */}
            {initialData && !initialData.hasAccount && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        Ученик ещё <strong>не зарегистрировался</strong>.
                        Контактные данные появятся после регистрации по ссылке-приглашению.
                    </Typography>
                </Alert>
            )}

            <Grid container spacing={2.5}>
                {/* ФИО */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="ФИО"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleTextChange}
                        error={!!errors.fullName}
                        helperText={errors.fullName}
                        placeholder="Иванов Иван Иванович"
                        disabled={loading}
                        autoFocus={!initialData}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Цена за урок */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Цена за урок"
                        name="pricePerLesson"
                        type="number"
                        value={formData.pricePerLesson || ''}
                        onChange={handleNumberChange}
                        error={!!errors.pricePerLesson}
                        helperText={errors.pricePerLesson || 'Стоимость одного занятия'}
                        disabled={loading}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MoneyIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                            htmlInput: {
                                min: 0,
                                step: 100,
                            },
                        }}
                    />
                </Grid>

                {/* Группа */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth error={!!errors.groupId}>
                        <InputLabel>Группа</InputLabel>
                        <Select
                            name="groupId"
                            value={formData.groupId ?? ''}
                            onChange={handleSelectChange}
                            label="Группа"
                            disabled={loading}
                            startAdornment={
                                <InputAdornment position="start">
                                    <GroupIcon color="action" />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="">
                                <em>Без группы</em>
                            </MenuItem>
                            {groups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.name} ({group.studentIds?.length || 0} уч.)
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.groupId && (
                            <FormHelperText>{errors.groupId}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Информация о группе */}
                {formData.groupId && (
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                            Выбранная группа: {getGroupName(formData.groupId)}
                        </Typography>
                    </Grid>
                )}

                {/* === КОНТАКТЫ — только если зарегистрирован === */}
                {showContacts && (
                    <>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                                Контактные данные
                            </Typography>
                        </Grid>

                        {/* Телефон */}
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Телефон"
                                name="phone"
                                value={formData.phone || ''}
                                onChange={handleTextChange}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                placeholder="+7 (999) 123-45-67"
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>

                        {/* VK */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="VK"
                                name="vk"
                                value={formData.vk || ''}
                                onChange={handleTextChange}
                                placeholder="https://vk.com/username"
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VkIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>

                        {/* Telegram */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Telegram"
                                name="telegram"
                                value={formData.telegram || ''}
                                onChange={handleTextChange}
                                placeholder="@username"
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TelegramIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>

                        {/* Email */}
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email || ''}
                                onChange={handleTextChange}
                                error={!!errors.email}
                                helperText={errors.email}
                                placeholder="example@mail.ru"
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
});

StudentForm.displayName = 'StudentForm';

export default StudentForm;